export default function OneAILogos() {
  const graphite = "#1E1E1E";
  const cyan = "#1CE3FF";
  const warmWhite = "#F7F7F7";
  const deepNavy = "#0A1A2F";
  const lime = "#B7F300";

  return (
    <div className="min-h-screen w-full bg-white text-slate-900 p-8">
      <style>{`
        @keyframes orbitSpin { from { transform: rotate(0deg); } to { transform: rotate(270deg); } }
      `}</style>
      <div className="max-w-6xl mx-auto grid gap-8">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">oneAI — Logo Concepts</h1>
            <p className="text-slate-600 mt-1">Directions #2 (Agent Orbit) and #1 (AV Logo). Each shown on light and dark, with icon-only and horizontal lockups.</p>
          </div>
        </header>

        {/* Agent Orbit */}
        <section className="grid gap-4">
          <h2 className="text-xl font-semibold">#2 – Agent Orbit</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Light */}
            <div className="rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-white p-6">
                <div className="flex items-center gap-4">
                  <AgentOrbitSVG mode="light" />
                  <div className="leading-tight">
                    <div className="font-bold text-2xl tracking-tight" style={{color: graphite}}>oneAI</div>
                    <div className="text-sm text-slate-500 -mt-0.5">consulting</div>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-3 gap-4 text-center">
                  <Tile title="Icon" bgClass="bg-white" border>
                    <AgentOrbitSVG mode="light" />
                  </Tile>
                  <Tile title="Lockup" bgClass="bg-white" border>
                    <LockupLeft icon={<AgentOrbitSVG mode="light" size={40} />} nameColor={graphite} subColor="#64748b" />
                  </Tile>
                  <Tile title="Favicon" bgClass="bg-white" border>
                    <AgentOrbitSVG mode="light" size={28} />
                  </Tile>
                </div>
              </div>
            </div>

            {/* Dark */}
            <div className="rounded-2xl border border-slate-800 shadow-sm overflow-hidden">
              <div className="p-6" style={{backgroundColor: deepNavy}}>
                <div className="flex items-center gap-4">
                  <AgentOrbitSVG mode="dark" />
                  <div className="leading-tight">
                    <div className="font-bold text-2xl tracking-tight" style={{color: warmWhite}}>oneAI</div>
                    <div className="text-sm" style={{color: "#93a4b8"}}>consulting</div>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-3 gap-4 text-center">
                  <Tile title="Icon" bgClass="" style={{backgroundColor: deepNavy}}>
                    <AgentOrbitSVG mode="dark" />
                  </Tile>
                  <Tile title="Lockup" bgClass="" style={{backgroundColor: deepNavy}}>
                    <LockupLeft icon={<AgentOrbitSVG mode="dark" size={40} />} nameColor={warmWhite} subColor="#93a4b8" />
                  </Tile>
                  <Tile title="Favicon" bgClass="" style={{backgroundColor: deepNavy}}>
                    <AgentOrbitSVG mode="dark" size={28} />
                  </Tile>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 1 → A Monogram */}
        <section className="grid gap-4">
          <h2 className="text-xl font-semibold">#1 – AV Logo</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Light */}
            <div className="rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-white p-6">
                <div className="flex items-center gap-4">
                  <OneASVG mode="light" />
                  <div className="leading-tight">
                    <div className="font-bold text-2xl tracking-tight" style={{color: graphite}}>oneAI</div>
                    <div className="text-sm text-slate-500 -mt-0.5">consulting</div>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-3 gap-4 text-center">
                  <Tile title="Icon" bgClass="bg-white" border>
                    <OneASVG mode="light" />
                  </Tile>
                  <Tile title="Lockup" bgClass="bg-white" border>
                    <LockupLeft icon={<OneASVG mode="light" size={40} />} nameColor={graphite} subColor="#64748b" />
                  </Tile>
                  <Tile title="Favicon" bgClass="bg-white" border>
                    <OneASVG mode="light" size={28} />
                  </Tile>
                </div>
              </div>
            </div>

            {/* Dark */}
            <div className="rounded-2xl border border-slate-800 shadow-sm overflow-hidden">
              <div className="p-6" style={{backgroundColor: graphite}}>
                <div className="flex items-center gap-4">
                  <OneASVG mode="dark" />
                  <div className="leading-tight">
                    <div className="font-bold text-2xl tracking-tight" style={{color: warmWhite}}>oneAI</div>
                    <div className="text-sm" style={{color: "#b2b9c5"}}>consulting</div>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-3 gap-4 text-center">
                  <Tile title="Icon" style={{backgroundColor: graphite}}>
                    <OneASVG mode="dark" />
                  </Tile>
                  <Tile title="Lockup" style={{backgroundColor: graphite}}>
                    <LockupLeft icon={<OneASVG mode="dark" size={40} />} nameColor={warmWhite} subColor="#b2b9c5" />
                  </Tile>
                  <Tile title="Favicon" style={{backgroundColor: graphite}}>
                    <OneASVG mode="dark" size={28} />
                  </Tile>
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer className="mt-4 text-sm text-slate-500">
          <p>Notes: Colors — Graphite {graphite}, Electric Cyan {cyan}, Warm White {warmWhite}, Deep Navy {deepNavy}, Lime {lime}. Icons are pure SVG and scale cleanly. Subtle motion on the Agent Orbit dot can be exported as a static asset as needed.</p>
        </footer>
      </div>
    </div>
  );
}

function Tile({ title, children, bgClass = "", border = false, style }: { title: string; children: React.ReactNode; bgClass?: string; border?: boolean; style?: React.CSSProperties }) {
  return (
    <div className={`rounded-xl ${border ? "border border-slate-200" : ""} p-4 flex flex-col items-center justify-center h-40 ${bgClass}`} style={style}>
      <div className="mb-2 text-xs uppercase tracking-wide text-slate-500">{title}</div>
      <div className="flex items-center justify-center">{children}</div>
    </div>
  );
}

function LockupLeft({ icon, nameColor = "#111827", subColor = "#6b7280" }: { icon: React.ReactNode; nameColor?: string; subColor?: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="shrink-0">{icon}</div>
      <div className="leading-tight">
        <div className="font-bold text-xl" style={{color: nameColor}}>oneAI</div>
        <div className="text-xs" style={{color: subColor}}>consulting</div>
      </div>
    </div>
  );
}

function AgentOrbitSVG({ mode = "light", size = 64 }: { mode?: "light" | "dark"; size?: number }) {
  const lime = "#B7F300";
  const stroke = mode === "dark" ? "#ffffff" : "#101316";
  const dot = mode === "dark" ? lime : "#2F6BFF"; // light mode accent blue for accessibility
  const tick = mode === "dark" ? lime : "#2F6BFF";

  const s = size;
  const c = s / 2;
  const r = s * 0.38;

  return (
    <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`} fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Orbit */}
      <circle cx={c} cy={c} r={r} stroke={stroke} strokeWidth={Math.max(2, s*0.06)} strokeLinecap="round" />
      {/* Action tick (90° corner) at ~4 o'clock */}
      <path d={`M ${c + r * 0.4} ${c + r * 0.45} L ${c + r * 0.85} ${c + r * 0.45} L ${c + r * 0.85} ${c + r * 0.9}`} stroke={tick} strokeWidth={Math.max(2, s*0.08)} strokeLinecap="round" strokeLinejoin="round" />
      {/* Agent dot that slowly orbits */}
      <g style={{ transformOrigin: `${c}px ${c}px`, animation: "orbitSpin 2.4s ease-in-out infinite" }}>
        <circle cx={c} cy={c - r} r={Math.max(2.2, s*0.06)} fill={dot} />
      </g>
      <title>Agent Orbit icon — autonomous agent completes an action</title>
    </svg>
  );
}

function OneASVG({ mode = "light", size = 64 }: { mode?: "light" | "dark"; size?: number }) {
  const graphite = "#1E1E1E";
  const websiteBlue = "rgb(96, 165, 250)"; // Light blue for the A left leg
  const warmWhite = "#F7F7F7";

  const s = size;
  const strokeWidth = Math.max(8, s * 0.125); // Adjust stroke width
  const cap = "round";

  const blueColor = websiteBlue; // Light blue for the A left leg
  const blackColor = mode === "dark" ? warmWhite : graphite; // Black for A right parts and V

  return (
    <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`} fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* A - Left leg (light blue) */}
      <line x1={s * 0.1875} y1={s * 0.15} x2={s * 0.0625} y2={s * 0.85} stroke={blueColor} strokeWidth={strokeWidth} strokeLinecap={cap} />
      {/* A - Left leg (black) */}
      <line x1={s * 0.4375} y1={s * 0.15} x2={s * 0.3125} y2={s * 0.85} stroke={blackColor} strokeWidth={strokeWidth} strokeLinecap={cap} />
      {/* A - Right leg (black) */}
      <line x1={s * 0.4375} y1={s * 0.15} x2={s * 0.5625} y2={s * 0.85} stroke={blackColor} strokeWidth={strokeWidth} strokeLinecap={cap} />
      {/* A - Crossbar (black) */}
      <line x1={s * 0.359375} y1={s * 0.55} x2={s * 0.515625} y2={s * 0.55} stroke={blackColor} strokeWidth={strokeWidth * 0.75} strokeLinecap={cap} />
      {/* V - Left leg (black) */}
      <line x1={s * 0.6875} y1={s * 0.15} x2={s * 0.8125} y2={s * 0.85} stroke={blackColor} strokeWidth={strokeWidth} strokeLinecap={cap} />
      {/* V - Right leg (black) */}
      <line x1={s * 0.9375} y1={s * 0.15} x2={s * 0.8125} y2={s * 0.85} stroke={blackColor} strokeWidth={strokeWidth} strokeLinecap={cap} />
      
      <title>AV Logo — Architectural Vision</title>
    </svg>
  );
}
